import {
  PropsWithChildren,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import 'ckeditor5/ckeditor5.css';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import { cva } from 'class-variance-authority';
import { BsInfoCircle } from 'react-icons/bs';
import Tooltip from 'ui/components/common/Tooltip';
import { SIZE, STATUS_TYPE } from 'utils/constants';
import { cn } from 'utils/helpers';
import 'assets/styles/Richtext.css';
import { Status } from 'ui/components/common';
import {
  DecoupledEditor,
  Bold,
  Essentials,
  Heading,
  Italic,
  Mention,
  TextTransformation,
  Underline,
  EditorConfig,
  Link,
  MentionConfig,
  Markdown,
  AutoLink,
} from 'ckeditor5';
import { size as _size, compact } from 'lodash';

import AttachFile from './CustomPlugins/AttachFile';
import customEvents from './customEvents';

export interface PROPS extends PropsWithChildren {
  className?: string | string[];
  size?: SIZE;
  textType?: 'default' | 'danger' | 'success' | 'warning';
  name?: string;
  label?: string;
  labelSubText?: string;
  subText?: string;
  required?: boolean;
  information?: string;
  richText?: boolean;
  value?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent<any, Element>) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  toolbar?: string[];
  onClickAttachButton?: () => void;
  onClickShowTemplates?: () => void;
  id?: string;
  rows?: number;
  isEmail?: boolean;
  textAreaClassName?: string;
  removeLink?: boolean;
  mention?: MentionConfig;
  hideToolbar?: boolean;
  markdown?: boolean;
}

const textareaClasses = cva(
  [
    'bg-fill-tertiary text-sm',
    'focus:outline-none focus:border-separator-secondary',
    'disabled:placeholder-disabled disabled:text-disabled disabled:border-disabled',
  ],
  {
    variants: {
      isEmail: {
        true: [],
        false: ['p-[14px] rounded', 'border border-separator-secondary'],
      },
      size: {
        default: ['w-full py-2'],
        [SIZE.Small]: [],
        [SIZE.Medium]: [],
        [SIZE.Large]: [],
      },
      textType: {
        default: ['placeholder-content', 'focus:border-primary-base'],
        success: [
          'placeholder-primary-base text-primary-base',
          '!border-primary-base',
          '!focus:border-primary-base',
        ],
        danger: [
          'placeholder-danger-base text-danger-base',
          '!border-danger-base',
          '!focus:border-danger-base',
        ],
        warning: [
          'placeholder-warning-base text-warning-base',
          '!border-warning-base',
          '!focus:border-warning-base',
        ],
      },
      isFocused: {
        true: ['border-primary-base'],
      },
    },
    defaultVariants: { size: 'default', textType: 'default' },
  },
);

const subTextClasses = cva(['text-xs'], {
  variants: {
    textType: {
      default: [],
      success: ['text-primary-base'],
      danger: ['text-danger-base'],
      warning: ['text-warning-base'],
    },
    size: {
      default: 'text-xs',
      [SIZE.Small]: 'text-xs',
      [SIZE.Medium]: 'text-xs',
      [SIZE.Large]: 'text-sm',
    },
  },
  defaultVariants: {
    textType: 'default',
  },
});

const Textarea = forwardRef<HTMLTextAreaElement, PROPS>(
  (
    {
      isEmail = false,
      className,
      size = SIZE.Small,
      textType,
      name,
      label,
      labelSubText = '(Optional)',
      subText,
      required = false,
      information,
      richText = false,
      value = '',
      disabled,
      onChange,
      onBlur,
      placeholder,
      toolbar = [],
      onClickAttachButton,
      onClickShowTemplates,
      children,
      id,
      rows,
      textAreaClassName,
      onKeyDown,
      removeLink = false,
      mention,
      hideToolbar,
      markdown,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const toolBarRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<HTMLDivElement>(null);

    const [isLayoutReady, setIsLayoutReady] = useState(false);

    useEffect(() => {
      setIsLayoutReady(true);

      return () => setIsLayoutReady(false);
    }, []);

    const { editorConfig } = useMemo<{ editorConfig: EditorConfig }>(() => {
      if (!isLayoutReady) {
        return { editorConfig: {} };
      }
      const LICENSE_KEY = 'GPL';
      return {
        editorConfig: {
          licenseKey: LICENSE_KEY,
          plugins: compact([
            Bold,
            Essentials,
            Heading,
            Italic,
            Link,
            AutoLink,
            Mention,
            TextTransformation,
            Underline,
            markdown && Markdown,
          ]),
          extraPlugins: [AttachFile],
          initialData: '',
          link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
            decorators: {
              toggleDownloadable: {
                mode: 'manual',
                label: 'Downloadable',
                attributes: {
                  download: 'file',
                },
              },
            },
          },
          list: {
            properties: {
              styles: true,
              startIndex: true,
              reversed: true,
            },
          },
          mention,
          toolbar: [
            'bold',
            'italic',
            'underline',
            !removeLink ? 'link' : '',
            onClickAttachButton ? 'attachFile' : '',
            ...toolbar,
          ],
          placeholder,
        },
      };
    }, [
      isLayoutReady,
      placeholder,
      removeLink,
      toolbar,
      mention,
      onClickAttachButton,
      markdown,
    ]);

    return (
      <div className="flex flex-col gap-y-3 w-full">
        {(!!label || !!information) && (
          <div className="text-sm font-semibold">
            {label && (
              <label htmlFor={name}>
                <span className="mr-1">{label}</span>
                {required ? (
                  <span className="text-danger-base">*</span>
                ) : (
                  <span className="text-xs text-content font-normal">
                    {labelSubText}
                  </span>
                )}
              </label>
            )}
            {!!information && (
              <Tooltip title={information} theme="dark">
                <div className="input-info">
                  <BsInfoCircle />
                </div>
              </Tooltip>
            )}
          </div>
        )}
        <div
          className={cn(
            textareaClasses({
              isEmail,
              size,
              textType,
              isFocused: isFocused || isHovered,
            }),
            className,
            'break-all',
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {richText && _size(editorConfig) ? (
            <div
              ref={editorRef}
              className={cn('rich-text-container', !hideToolbar && 'pb-8 ')}
            >
              <CKEditor
                editor={DecoupledEditor}
                data={value}
                onChange={(_, editor) => {
                  if (editor) {
                    onChange?.(editor?.getData() as string);
                  }
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                config={{
                  ...editorConfig,
                }}
                disabled={disabled}
                onReady={(editor) => {
                  setTimeout(() => {
                    if (toolBarRef.current && !hideToolbar) {
                      toolBarRef.current.appendChild(
                        editor.ui.view.toolbar.element as Node,
                      );
                    }
                  }, 100);
                  editor.on(customEvents.openFileDialog, () => {
                    onClickAttachButton?.();
                  });
                }}
              />
              {children}
              <div
                className="flex items-center rich-text-toolbar w-full"
                ref={toolBarRef}
              >
                {onClickShowTemplates && (
                  <div className="flex flex-1 absolute right-0 mr-[-25px]">
                    <Status
                      className="cursor-pointer hover:opacity-50 transition"
                      type={STATUS_TYPE.Pending}
                      id="composer-attachment"
                      label="Templates"
                      onClick={onClickShowTemplates}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <textarea
              ref={ref}
              name={name}
              value={value}
              disabled={disabled}
              onChange={(e) => onChange?.(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={(e) => {
                onBlur?.(e);
                setIsFocused(false);
              }}
              placeholder={placeholder}
              className={cn(
                'w-full bg-transparent',
                'focus:outline-none',
                textAreaClassName,
              )}
              id={id}
              rows={rows}
              onKeyDown={onKeyDown}
              {...props}
            />
          )}
        </div>
        {subText && (
          <div className={cn(subTextClasses({ textType, size }))}>
            {subText}
          </div>
        )}
      </div>
    );
  },
);

export default Textarea;
